<script lang="ts">
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import ButtonSmallBlue from '../../shared/ButtonSmallBlue.svelte';
    import IconPending from '../../shared/Icons/IconPending.svelte';
    import { api } from '../../../stores/api';
    import { JobModifyTimeWindowRuleApi, type JobModifyTimeWindowRuleResponseType } from './JobModifyTimeWindowRuleApi';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import type { JobResponseType } from '../Jobs/types';
    import MenuContextIcon from '../MenuDropdown/MenuContext/MenuContextIcon.svelte';
    import { MenuContextCustomActions } from '../MenuDropdown/MenuContextCustomActions';
    import IconCopy from '../../shared/Icons/IconCopy.svelte';
    import { TacticalPlanRule } from '../TacticalPlan/TacticalPlanRule';
    import IconStylus from '../../shared/Icons/IconStylus.svelte';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import { _ } from 'svelte-i18n';

    export let job: JobResponseType;
    export let onRuleSaved: () => void = () => {};

    let loading: boolean = true;
    let error: string | null = null;
    let existingRule: JobModifyTimeWindowRuleResponseType | null = null;
    let isSaving: boolean = false;
    let hasChanges: boolean = false;
    let initialStartTime: string | null = null;
    let initialEndTime: string | null = null;
    let initialNamePart: string | null = null;

    // Sprawdza czy job ma address_id
    $: canSaveRule = job.address_id !== null && job.address_id !== undefined;

    // Sprawdza czy są zmiany w oknach czasowych
    $: currentStart = job.special_attributes?.start_of_time_window || null;
    $: currentEnd = job.special_attributes?.end_of_time_window || null;
    $: currentNamePart = job.special_attributes?.label || null;
    $: hasChanges = (currentStart !== initialStartTime || currentEnd !== initialEndTime || initialNamePart !== currentNamePart) && canSaveRule;

    async function loadTimeWindowRule() {
        if (!canSaveRule) {
            loading = false;
            return;
        }

        try {
            loading = true;
            error = null;

            // Pobierz reguły dla tego address_id
            const filter = new ApiFilterBuilder(null);
            filter.addFilterByNumber('address_id', job.address_id!);
            filter.addFilterByString('name_part', job?.special_attributes?.label ?? '');

            const rules = await JobModifyTimeWindowRuleApi.filter(get(api), filter);

            if (rules && rules.length > 0) {
                existingRule = rules[0]; // Bierzemy pierwszą regułę
                initialStartTime = existingRule.start_of_time_window;
                initialEndTime = existingRule.end_of_time_window;
                initialNamePart = existingRule.name_part;
            } else {
                existingRule = null;
                initialStartTime = job.special_attributes?.start_of_time_window || null;
                initialEndTime = job.special_attributes?.end_of_time_window || null;
                initialNamePart = job.special_attributes?.label || null;
            }
        } catch (err: any) {
            console.error('Error loading time window rule:', err);
            error = err.message || 'Failed to load time window rule';
            initialStartTime = job.special_attributes?.start_of_time_window || null;
            initialEndTime = job.special_attributes?.end_of_time_window || null;
            initialNamePart = job.special_attributes?.label || null;
        } finally {
            loading = false;
            updateMenu(menu);
        }
    }

    async function saveRule() {
        if (!canSaveRule || isSaving) {
            return;
        }

        try {
            isSaving = true;
            error = null;

            const ruleData = {
                enabled: true,
                address_id: job.address_id,
                name_part: job.special_attributes?.label || null,
                start_of_time_window: job.special_attributes?.start_of_time_window || null,
                end_of_time_window: job.special_attributes?.end_of_time_window || null,
            };

            if (existingRule) {
                // Aktualizuj istniejącą regułę
                await JobModifyTimeWindowRuleApi.updateOne(get(api), existingRule.id, ruleData);
                console.log('Time window rule updated for address_id:', job.address_id);
            } else {
                // Utwórz nową regułę
                const newRule = await JobModifyTimeWindowRuleApi.createOne(get(api), ruleData);
                existingRule = newRule;
                console.log('Time window rule created for address_id:', job.address_id);
            }

            // Zaktualizuj początkowe wartości
            initialStartTime = ruleData.start_of_time_window;
            initialEndTime = ruleData.end_of_time_window;
            initialNamePart = ruleData.name_part;

            // Wywołaj callback
            onRuleSaved();
        } catch (err: any) {
            console.error('Error saving time window rule:', err);
            error = err.message || 'Failed to save time window rule';
        } finally {
            isSaving = false;
            updateMenu(menu);
        }
    }

    onMount(() => {
        console.log('JobModifyTimeWindowRuleMiniWidget mounted for job:', job.id, 'address_id:', job.address_id);
        loadTimeWindowRule();
    });


    let menu: MenuContextCustomActions = new MenuContextCustomActions();

    function updateMenu(menu: MenuContextCustomActions) {
        menu.reset();
        menu.addAction({
            id: 'remove',
            label: $_('remove_rule'),
            disabled: existingRule === null,
            visible: true,
            icon: IconRemove,
            onClick: () => {
                if (confirm($_('confirm_remove_rule'))) {
                    JobModifyTimeWindowRuleApi.removeOne(get(api), existingRule!.id)
                        .then(() => {
                            existingRule = null;
                            initialStartTime = null;
                            initialEndTime = null;
                            initialNamePart = null;
                            loadTimeWindowRule();
                        })
                        .catch(err => {
                            alert($_('remove_rule_failed'));
                        });
                }
            },
            subMenuIsOpen: false,
            visibleInContextMenu: true,
        });
        menu = menu;
    }


</script>

{#if !canSaveRule}
    <div>
        {$_('address_incomplete_cannot_save_rule')}
    </div>
{:else if loading}
    <IconPending spin={true} scale={0.6} />
{:else if error}
    <MessageDanger {error} />
{:else}
    <div style="display: flex; justify-content: flex-start; align-items: center;">
        <div>
            <ButtonSmallBlue
                text={$_('save_rule')}
                hover={$_('save_address_rule_permanently')}
                disabled={!hasChanges || isSaving}
                on:click={saveRule}
            />
        </div>
        <div>
            <MenuContextIcon bind:menu />
        </div>
    </div>
{/if}
